import { ROLES, type Role } from '@easyhelp/shared';
// Con l'estensione: anche vite.config.ts carica questo modulo (avvisi della Guida nella build).
import { GUIDE_ROUTES } from './screens.ts';

/** Tipi di contenuto della Guida, nell'ordine dell'indice (DEC-011 punto 2). */
export const GUIDE_TYPES = ['primi-passi', 'procedura', 'riferimento', 'concetto', 'faq'] as const;
export type GuideType = (typeof GUIDE_TYPES)[number];

/** Sezioni delle FAQ, nell'ordine dell'indice. */
export const FAQ_SECTIONS = ['domande-frequenti', 'risoluzione-problemi'] as const;
export type FaqSection = (typeof FAQ_SECTIONS)[number];

/** Il front-matter di una pagina, già validato. */
export interface FrontMatter {
  titolo: string;
  tipo: GuideType;
  /** Solo per `tipo: faq`. */
  sezione?: FaqSection;
  ruoli: Role[];
  schermate: string[];
  /**
   * Schermate (tra quelle di `schermate`) di cui la pagina è la principale: il loro «?» apre questa
   * pagina invece della prima procedura. Facoltativo; mai per le voci FAQ.
   */
  principale: string[];
  story: string;
  /** Codici del catalogo spiegati dalla voce: solo nella Risoluzione dei problemi. */
  codici: string[];
}

/** Esito della lettura: il front-matter e il testo che segue, oppure gli errori trovati. */
export type FrontMatterResult = { ok: true; data: FrontMatter; body: string } | { ok: false; errors: string[] };

type RawValue = string | string[];

const KNOWN_KEYS = ['titolo', 'tipo', 'sezione', 'ruoli', 'schermate', 'principale', 'story', 'codici'] as const;
const FRONT_MATTER = /^---\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;

/** Toglie un commento `# …` che non sta tra virgolette. */
function stripComment(value: string): string {
  let quote: string | null = null;
  for (let index = 0; index < value.length; index++) {
    const char = value[index];
    if (quote) {
      if (char === quote) quote = null;
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === '#' && (index === 0 || /\s/.test(value[index - 1] ?? ''))) {
      return value.slice(0, index);
    }
  }
  return value;
}

function unquote(value: string): string {
  const trimmed = value.trim();
  const first = trimmed[0];
  if (trimmed.length >= 2 && (first === '"' || first === "'") && trimmed.endsWith(first)) return trimmed.slice(1, -1);
  return trimmed;
}

/**
 * Il sottoinsieme di YAML delle convenzioni: `chiave: valore`, `chiave: "valore"` e liste in riga
 * `chiave: [a, b]`, con commenti `#`. Tutto il resto è un errore, non un valore ignorato.
 */
function parseYamlSubset(source: string): { values: Map<string, RawValue>; errors: string[] } {
  const values = new Map<string, RawValue>();
  const errors: string[] = [];
  source.split(/\r?\n/).forEach((line, index) => {
    if (!line.trim() || line.trim().startsWith('#')) return;
    const match = /^([a-z]+):(.*)$/.exec(line);
    if (!match) {
      errors.push(`riga ${index + 2} del front-matter non leggibile: «${line.trim()}»`);
      return;
    }
    const [, key = '', rest = ''] = match;
    if (values.has(key)) errors.push(`«${key}» compare due volte`);
    const value = stripComment(rest).trim();
    if (value.startsWith('[')) {
      if (!value.endsWith(']')) {
        errors.push(`«${key}» va scritto come lista in riga, es. [a, b]`);
        return;
      }
      const items = value.slice(1, -1).split(',').map(unquote).filter(Boolean);
      values.set(key, items);
    } else {
      values.set(key, unquote(value));
    }
  });
  return { values, errors };
}

function isOneOf<T extends string>(list: readonly T[], value: string): value is T {
  return (list as readonly string[]).includes(value);
}

/** Divide il Markdown in front-matter grezzo e testo; `null` se il front-matter manca. */
export function splitFrontMatter(markdown: string): { raw: string; body: string } | null {
  const match = FRONT_MATTER.exec(markdown);
  if (!match) return null;
  return { raw: match[1] ?? '', body: markdown.slice(match[0].length) };
}

/**
 * Legge e valida il front-matter di una pagina della Guida secondo le convenzioni di
 * `docs/manuale/operatori/README.md`. Gli errori sono frasi da mostrare a chi scrive la pagina.
 */
export function parseFrontMatter(markdown: string): FrontMatterResult {
  const split = splitFrontMatter(markdown);
  if (!split) return { ok: false, errors: ['manca il front-matter tra due righe «---» in testa al file'] };
  const { values, errors } = parseYamlSubset(split.raw);

  for (const key of values.keys()) {
    if (!isOneOf(KNOWN_KEYS, key)) errors.push(`campo sconosciuto «${key}»`);
  }
  const text = (key: string, required: boolean): string | undefined => {
    const value = values.get(key);
    if (value === undefined) {
      if (required) errors.push(`manca «${key}»`);
      return undefined;
    }
    if (typeof value !== 'string' || !value) {
      errors.push(`«${key}» deve essere un testo non vuoto`);
      return undefined;
    }
    return value;
  };
  const list = (key: string, required: boolean): string[] => {
    const value = values.get(key);
    if (value === undefined) {
      if (required) errors.push(`manca «${key}»`);
      return [];
    }
    if (typeof value === 'string') {
      errors.push(`«${key}» va scritto come lista in riga, es. [a, b]`);
      return [];
    }
    return value;
  };

  const titolo = text('titolo', true);
  const tipo = text('tipo', true);
  if (tipo !== undefined && !isOneOf(GUIDE_TYPES, tipo)) errors.push(`tipo «${tipo}» non previsto`);
  const story = text('story', true);
  if (story !== undefined && !/^#\d+$/.test(story)) errors.push(`story «${story}» va scritta come "#123"`);

  const ruoli = list('ruoli', true);
  if (Array.isArray(values.get('ruoli')) && ruoli.length === 0) errors.push('«ruoli» non può essere vuoto');
  for (const ruolo of ruoli) if (!isOneOf(ROLES, ruolo)) errors.push(`ruolo «${ruolo}» non previsto`);
  const schermate = list('schermate', true);
  for (const route of schermate) if (!GUIDE_ROUTES.includes(route)) errors.push(`schermata «${route}» non prevista`);
  const principale = list('principale', false);
  for (const route of principale) {
    if (!schermate.includes(route)) errors.push(`«principale» indica «${route}», che non è tra le «schermate»`);
  }
  if (tipo === 'faq' && principale.length > 0) errors.push('«principale» non vale per le voci FAQ');
  const codici = list('codici', false);

  const sezione = text('sezione', tipo === 'faq');
  if (tipo === 'faq' && sezione !== undefined && !isOneOf(FAQ_SECTIONS, sezione)) {
    errors.push(`sezione «${sezione}» non prevista`);
  }
  if (tipo !== 'faq' && values.has('sezione')) errors.push('«sezione» vale solo per le voci FAQ');
  if (values.has('codici') && sezione !== 'risoluzione-problemi') {
    errors.push('«codici» vale solo per le voci di Risoluzione dei problemi');
  }

  if (errors.length > 0 || !titolo || !tipo || !story || !isOneOf(GUIDE_TYPES, tipo)) {
    return { ok: false, errors };
  }
  return {
    ok: true,
    data: {
      titolo,
      tipo,
      ...(sezione !== undefined && isOneOf(FAQ_SECTIONS, sezione) ? { sezione } : {}),
      ruoli: ruoli.filter((ruolo): ruolo is Role => isOneOf(ROLES, ruolo)),
      schermate,
      principale,
      story,
      codici,
    },
    body: split.body,
  };
}
